import { useEffect, useState, type ReactNode } from "react";
import PageBackBar from "../components/PageBackBar";
import { useAuth } from "../features/auth/useAuth";
import { DEFAULT_PAGE_SIZE, DirectoryPagination } from "../features/directory/DirectoryPagination";
import { extractAxiosDetail, formatAbsolute, formatRelative, humanizeToken } from "../features/directory/format";
import { useDirectoryAudit, useDirectoryDrift, useWorkvivoDirectoryRefresh } from "../features/directory/hooks";
import type { DirectoryAuditEntry, DriftStore } from "../features/directory/types";
import WorkvivoSyncBar from "../features/directory/WorkvivoSyncBar";
import "../App.css";

const AUDIT_ACTION_LABELS: Record<string, string> = {
  deactivate_workvivo_user: "Deactivated Workvivo user",
  resend_welcome_email: "Resent welcome email",
  assign_store_owner: "Assigned location admin"
};

function auditActionLabel(action: string) {
  return AUDIT_ACTION_LABELS[action] ?? humanizeToken(action);
}

function HealthSection({
  title,
  count,
  hint,
  children
}: {
  title: string;
  count?: number;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="directory-panel">
      <div className="directory-panel-head">
        <h2 className="directory-panel-title">
          {title}
          {count !== undefined ? <span className="directory-panel-count">{count}</span> : null}
        </h2>
      </div>
      {hint ? <p className="directory-panel-hint">{hint}</p> : null}
      {children}
    </section>
  );
}

function NoDrift() {
  return <p className="directory-panel-empty">No drift found.</p>;
}

function DriftStoreTable({ stores }: { stores: DriftStore[] }) {
  if (stores.length === 0) return <NoDrift />;
  return (
    <div className="directory-table-wrap directory-table-wrap--flush">
      <table className="directory-table">
        <thead>
          <tr>
            <th scope="col">Location</th>
            <th scope="col">Catalog location</th>
            <th scope="col">Concept</th>
            <th scope="col">Admin</th>
          </tr>
        </thead>
        <tbody>
          {stores.map((s) => (
            <tr key={s.store_id}>
              <td data-label="Location" className="directory-cell-primary">
                <div className="directory-person">
                  <span className="directory-person-name">{s.business_name}</span>
                  <span className="directory-person-email">{s.store_id}</span>
                  {s.is_active ? null : (
                    <span className="directory-badge-row">
                      <span className="directory-badge directory-badge--inactive">Inactive</span>
                    </span>
                  )}
                </div>
              </td>
              <td data-label="Catalog">
                {s.catalog_location ? (
                  <span className="directory-cell-text">{s.catalog_location}</span>
                ) : (
                  <span className="directory-muted-italic">Not set</span>
                )}
              </td>
              <td data-label="Concept">
                <span className={s.catalog_concept ? "directory-cell-text" : "directory-muted"}>
                  {s.catalog_concept || "—"}
                </span>
              </td>
              <td data-label="Admin">
                {s.owner_email ? (
                  <span className="directory-cell-text">{s.owner_email}</span>
                ) : (
                  <span className="directory-muted-italic">Unassigned</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTarget({ entry }: { entry: DirectoryAuditEntry }) {
  const label = entry.target_label || entry.target_id;
  return (
    <div className="directory-stack">
      <span className={label ? "directory-cell-text" : "directory-muted"}>{label || "—"}</span>
      {entry.target_label && entry.target_id && entry.target_id !== entry.target_label ? (
        <span className="directory-muted-small">{entry.target_id}</span>
      ) : null}
    </div>
  );
}

function RecentActions() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const query = useDirectoryAudit({ limit: pageSize, offset: page * pageSize });

  useEffect(() => {
    const d = query.data;
    if (query.isFetching || !d) return;
    if (d.total > 0 && d.items.length === 0 && d.offset >= d.total) setPage(0);
  }, [query.data, query.isFetching]);

  const data = query.data;
  const items = data?.items ?? [];

  return (
    <HealthSection title="Recent actions" count={data?.total}>
      {query.isLoading ? (
        <div className="directory-state" role="status">
          Loading recent actions…
        </div>
      ) : query.isError ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load recent actions.</strong> {extractAxiosDetail(query.error)}
        </div>
      ) : items.length === 0 ? (
        <p className="directory-panel-empty">No actions recorded yet.</p>
      ) : (
        <>
          <div
            className="directory-table-wrap directory-table-wrap--flush"
            aria-busy={query.isPlaceholderData}
            data-stale={query.isPlaceholderData || undefined}
          >
            <table className="directory-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">Action</th>
                  <th scope="col">Target</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td data-label="When">
                      <span title={formatAbsolute(entry.created_at)}>{formatRelative(entry.created_at) || "—"}</span>
                    </td>
                    <td data-label="Who">
                      <div className="directory-stack">
                        <span className={entry.actor_email ? "directory-cell-text" : "directory-muted"}>
                          {entry.actor_email || "—"}
                        </span>
                        {entry.actor_role ? (
                          <span className="directory-muted-small">{humanizeToken(entry.actor_role)}</span>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="Action">
                      <span
                        className={`directory-badge${
                          entry.action === "deactivate_workvivo_user" ? " directory-badge--inactive" : ""
                        }`}
                      >
                        {auditActionLabel(entry.action)}
                      </span>
                    </td>
                    <td data-label="Target">
                      <AuditTarget entry={entry} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DirectoryPagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            offset={data?.offset ?? 0}
            itemCount={items.length}
            noun="actions"
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
          />
        </>
      )}
    </HealthSection>
  );
}

export default function DirectoryHealth() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const workvivoRefresh = useWorkvivoDirectoryRefresh();
  const drift = useDirectoryDrift({ enabled: isSuperuser });

  if (!user || !isSuperuser) {
    return <div style={{ color: "#b91c1c" }}>You don’t have access to view directory health.</div>;
  }

  const data = drift.data;

  return (
    <div className="directory-page">
      <PageBackBar to="/location-admins" label="Back to location admins" />
      <header>
        <h1 className="approvals-page-title">Directory health</h1>
        <p className="approvals-page-sub">
          Where the portal’s locations and the cached Workvivo directory disagree, plus a log of recent directory
          actions taken from this portal.
        </p>
      </header>

      {data ? <WorkvivoSyncBar sync={data.sync} refresh={workvivoRefresh} /> : null}

      {drift.isLoading ? (
        <div className="directory-state" role="status">
          Checking directory health…
        </div>
      ) : drift.isError ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load directory health.</strong> {extractAxiosDetail(drift.error)}
        </div>
      ) : data ? (
        <>
          <section className="directory-kpis" aria-label="Totals">
            <div className="directory-kpi">
              <p className="directory-kpi-label">Workvivo users</p>
              <p className="directory-kpi-value">{data.totals.snapshot_users}</p>
              <p className="directory-kpi-sub">In the cached snapshot</p>
            </div>
            <div className="directory-kpi">
              <p className="directory-kpi-label">Active in Workvivo</p>
              <p className="directory-kpi-value">{data.totals.snapshot_active}</p>
              <p className="directory-kpi-sub">
                {data.totals.snapshot_users - data.totals.snapshot_active} deactivated
              </p>
            </div>
            <div className="directory-kpi">
              <p className="directory-kpi-label">Locations</p>
              <p className="directory-kpi-value">{data.totals.stores}</p>
              <p className="directory-kpi-sub">Stores in the portal</p>
            </div>
          </section>

          <HealthSection
            title="Workvivo locations with no matching store"
            count={data.unmatched_localities.length}
            hint="A store’s catalog location must exactly equal the Workvivo locality for its people to be matched. Fix the store’s catalog location (or the locality in Workvivo) and refresh."
          >
            {data.unmatched_localities.length === 0 ? (
              <NoDrift />
            ) : (
              <div className="directory-table-wrap directory-table-wrap--flush">
                <table className="directory-table">
                  <thead>
                    <tr>
                      <th scope="col">Workvivo locality</th>
                      <th scope="col" className="directory-col-num">
                        Users
                      </th>
                      <th scope="col" className="directory-col-num">
                        Active
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unmatched_localities.map((l) => (
                      <tr key={l.locality}>
                        <td data-label="Locality" className="directory-cell-primary">
                          <span className="directory-person-name">{l.locality}</span>
                        </td>
                        <td data-label="Users" className="directory-col-num">
                          <span className="directory-count">{l.user_count}</span>
                        </td>
                        <td data-label="Active" className="directory-col-num">
                          <span className="directory-count">{l.active_count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </HealthSection>

          <HealthSection
            title="Stores with no Workvivo users"
            count={data.stores_without_users.length}
            hint="These stores have a catalog location, but no one in Workvivo has that locality."
          >
            <DriftStoreTable stores={data.stores_without_users} />
          </HealthSection>

          <HealthSection
            title="Stores missing a catalog location"
            count={data.unmapped_stores.length}
            hint="Without a catalog location, a store’s Workvivo users can’t be matched to it."
          >
            <DriftStoreTable stores={data.unmapped_stores} />
          </HealthSection>

          <HealthSection title="Users with no location" hint="Workvivo users whose locality field is empty.">
            {data.users_without_locality.user_count === 0 ? (
              <NoDrift />
            ) : (
              <p className="directory-panel-stat">
                <span className="directory-count">{data.users_without_locality.user_count}</span> user
                {data.users_without_locality.user_count === 1 ? "" : "s"} (
                <span className="directory-count">{data.users_without_locality.active_count}</span> active) have no
                locality in Workvivo.
              </p>
            )}
          </HealthSection>
        </>
      ) : null}

      <RecentActions />
    </div>
  );
}
